/* =========================================================================
   AnalysisStudioLive — live data builders.

   Assemble `Studio2Player` / `Studio2Session` from the REAL Redux store for the
   GREEN subcomponents (risk/return scatter, risk evolution charts + tiles,
   behaviour). Every field is produced from a fully-shaped *neutral skeleton*,
   then the wired fields are overlaid with real data — so the not-yet-wired
   panels render empty/placeholder instead of crashing on `undefined`
   (e.g. DecisionsPanel dereferences `report.investments.*` with no guards).

   Pure functions only — no React, no store access here.
   ========================================================================= */
import type {
  Player,
  Session,
  IndividualReport,
  Metrics,
  PlayerAnalysisType,
  PromptType,
  LLMResponseType,
  LLMGroupResponseType,
  BiasReportType,
  LeaderboardScoresType,
  Order,
  UnderlyingAsset,
  Currency,
  MarketRegimeSegmentType,
} from 'state';
import {
  HISTORICAL_SCENARIO,
  GROUP_OF_ASSET,
  GROUP_ASSET_NAME,
  instrumentTypes,
  CREATED,
  EXECUTED,
  BUY,
} from 'state';
import {
  isCompletedLLMResponse,
  isCompletedLLMGroupResponse,
} from '../../AI/aiReportShared';
import { REGIME_META } from '../helpers';
import type { RelativizeDate } from '../helpers';
import type {
  Studio2Player,
  Studio2Session,
  Studio2AIReport,
  Studio2BiasReport,
  Studio2BlotterRow,
  Studio2OpenOrderRow,
  TradeAttribute,
} from './types';
import {
  simplifiedOptionSnapshotActionLabel,
  simplifiedOptionSnapshotContracts,
} from '../../../../state/app/simplified_options/presentation';

/** `IndividualReport.status` value meaning "computed" (mirrors
 *  reusable/IndividualReport/constants UNAVAILABLE=0 | COMPUTED=1 | ERROR=2). */
const REPORT_COMPUTED = 1;

/** Backend `band` is a CharField 'A'|'B'|'C'|'D' (or null), but the studio's
 *  helpers/BandChip index a 0-based array (A=0…D=3). Normalise letters → index;
 *  pass through any numeric (mock/legacy is already 0-based); else null. (W1) */
const BAND_LETTER_INDEX: Record<string, number> = { A: 0, B: 1, C: 2, D: 3 };
export const normalizeBand = (
  band: number | string | null | undefined,
): number | null => {
  if (band == null) return null;
  if (typeof band === 'number') return band;
  const idx = BAND_LETTER_INDEX[band.toUpperCase()];
  return idx == null ? null : idx;
};

const NEUTRAL_METRICS: Metrics = {
  id: 0,
  timestamp: '',
  scenario_date: null,
  portfolio_volatility: 0,
  alpha: 0,
  beta: 0,
  sharpe_ratio: 0,
  treynor_ratio: 0,
  var05: 0,
  market_index_return: 0,
  mdd: 0,
};

const ZERO_STATS = { min: 0, max: 0, avg: 0 };

/** Fully-shaped, all-zero report so non-green panels never read `undefined`. */
export const NEUTRAL_REPORT: IndividualReport = {
  player: 0,
  session: 0,
  status: 0,
  returns: {
    returns_percent: 0,
    gross_profit: 0,
    gross_loss: 0,
    total_net_return: 0,
    unrealized_return: 0,
    realized_return: 0,
    fx_return: 0,
    rank: 0,
  },
  returns_breakdown: {
    return_per_geography: {},
    return_per_asset_class: {},
    return_per_instrument: {},
  },
  return_per_trade: {
    avg_profit: 0,
    avg_loss: 0,
    max_realized_profit: 0,
    max_realized_loss: 0,
    min_realized_profit: 0,
    min_realized_loss: 0,
    max_unrealized_profit: 0,
    max_unrealized_loss: 0,
    min_unrealized_profit: 0,
    min_unrealized_loss: 0,
    position_pnl_avg: 0,
  },
  trades: {
    total: 0,
    daily_avg: 0,
    daily_max: 0,
    intraday_percent: 0,
    profitable_percent: 0,
    losing_percent: 0,
  },
  hold_period: {
    min_hold: 0,
    max_hold: 0,
    avg_hold: 0,
    trades_percent: 0,
    holds_per_asset: {},
    hold_per_winners: { ...ZERO_STATS },
    hold_per_losers: { ...ZERO_STATS },
    hold_per_longs: { ...ZERO_STATS },
    hold_per_shorts: { ...ZERO_STATS },
  },
  investments: {
    biggest_winner: {
      instrument: '—',
      executed_at: '',
      quantity: 0,
      open_price: 0,
      close_price: 0,
      value: 0,
    },
    biggest_loser: {
      instrument: '—',
      executed_at: '',
      quantity: 0,
      open_price: 0,
      close_price: 0,
      value: 0,
    },
    top_long_position: {
      instrument: '—',
      created_at: '',
      closed_at: undefined,
      value: 0,
    },
    top_short_position: {
      instrument: '—',
      created_at: '',
      closed_at: undefined,
      value: 0,
    },
  },
};

/** Build one `Studio2Player`: real wired fields overlaid on a neutral skeleton.
 *  `analysis` (the coach-read list) is wired from the session-level, all-players
 *  analysis array when available, with bands normalised to numeric indices.
 *  Still-neutral fields stay empty until their own chunks. */
export const neutralStudio2Player = (
  p: Player,
  analysis?: PlayerAnalysisType,
  startingCash = 0,
): Studio2Player => ({
  // identity + headline (real)
  id: p.id,
  name: p.name,
  online: p.online,
  deals: p.deals ?? 0,
  last_market_value: p.last_market_value ?? 0,
  last_pnl_sum: p.last_pnl_sum ?? 0,
  // `last_fx_return` is a monetary FX P&L; express it as a return fraction
  // (÷ starting cash) so the studio's `pct()` matches the trainee FX-return %.
  last_fx_return: startingCash ? (p.last_fx_return ?? 0) / startingCash : 0,
  last_portfolio_return: p.last_portfolio_return ?? 0,
  latest_afs_dv01: p.latest_afs_dv01 ?? null,
  afs_dv01_history: p.afs_dv01_history ?? [],
  // scatter + risk tiles + evolution charts (real)
  last_metrics: p.last_metrics ?? NEUTRAL_METRICS,
  metrics: p.metrics ?? [],
  player_history: p.player_history ?? [],
  display_series: p.display_series ?? [],
  // coach-read analysed metrics (real, band letter → numeric index)
  analysis: analysis
    ? {
        player_id: analysis.player_id,
        player_name: analysis.player_name,
        analyzed_metrics: analysis.analyzed_metrics.map(am => ({
          ...am,
          band: normalizeBand(am.band),
        })),
      }
    : { player_id: p.id, player_name: p.name, analyzed_metrics: [] },
  // tactical allocation pie (real — grouped from tactical_asset_allocations)
  allocations: buildAllocations(p),
  // long/short two-pie split (same source, split by book)
  ...buildAllocationsLongShort(p),
  // Gross P&L bridge (realized + unrealized + FX) — cohort-wide, from the
  // analysis flow (all players, not just the focused one). null until computed.
  gross_pnl: analysis?.gross_pnl ?? {
    gross_profit: 0,
    gross_loss: 0,
    total_net_return: 0,
  },
  // Per-asset-class turnover — overlaid for every player in useStudio2Data; empty
  // skeleton here so the tile/headline render 0 until the turnover flow has run.
  asset_class_turnover: [],
  total_turnover: 0,
  // non-green (neutral until later chunks)
  report: NEUTRAL_REPORT,
  pnl_distribution: [],
  concentration: [],
  blotter: [],
  open_orders: [],
  ai_reports: [],
  // Bias analysis is loaded separately (fetch_bias_reports) and overlaid in
  // useStudio2Data; null renders the panel's "no analysis yet" empty state.
  bias: null,
});

/** Group `tactical_asset_allocations` into the studio's allocation buckets
 *  (mirrors v1's getAllocations): sum `percent` by asset-class group, named via
 *  GROUP_ASSET_NAME (with a fallback for groups lacking a name, e.g. options).
 *  Falls back to all-cash when a player has no allocation rows. */
export const buildAllocations = (p: Player): Studio2Player['allocations'] => {
  const grouped = (p.tactical_asset_allocations ?? []).reduce(
    (acc, a) => {
      const groupIdx = GROUP_OF_ASSET[a.asset_class];
      const name = GROUP_ASSET_NAME[groupIdx] || `Group ${groupIdx}`;
      acc[name] = (acc[name] ?? 0) + a.percent;
      return acc;
    },
    {} as Record<string, number>,
  );
  if (!Object.keys(grouped).length) grouped.Cash = 1;
  return grouped as Studio2Player['allocations'];
};

/** Build the Long-book / Short-book groupings for the two-pie split. Each book
 *  is normalised WITHIN itself off the absolute `long` / `|short|` group sums
 *  (so each pie is self-contained and sums to 1, independent of the net total).
 *  `percent_long` / `percent_short` are intentionally NOT used — they are
 *  net-portfolio fractions. `Math.abs` guards against either sign convention for
 *  `short`. `*_total` are each book's absolute reporting value for the caption.
 *  `*_abs` are the per-group absolute reporting values (NOT normalised) — these
 *  feed the Long/Short tornado bar, whose bar lengths must convey real book
 *  magnitude (which the within-book-normalised pies deliberately flatten). */
export const buildAllocationsLongShort = (
  p: Player,
): Pick<
  Studio2Player,
  | 'allocations_long'
  | 'allocations_short'
  | 'allocations_long_total'
  | 'allocations_short_total'
  | 'allocations_long_abs'
  | 'allocations_short_abs'
> => {
  const longByGroup: Record<string, number> = {};
  const shortByGroup: Record<string, number> = {};
  for (const a of p.tactical_asset_allocations ?? []) {
    const groupIdx = GROUP_OF_ASSET[a.asset_class];
    const name = GROUP_ASSET_NAME[groupIdx] || `Group ${groupIdx}`;
    longByGroup[name] = (longByGroup[name] ?? 0) + Math.abs(a.long ?? 0);
    shortByGroup[name] = (shortByGroup[name] ?? 0) + Math.abs(a.short ?? 0);
  }
  const sumLong = Object.values(longByGroup).reduce((s, v) => s + v, 0);
  const sumShort = Object.values(shortByGroup).reduce((s, v) => s + v, 0);
  const normalise = (byGroup: Record<string, number>, sum: number) => {
    const out: Record<string, number> = {};
    if (sum <= 0) return out;
    for (const [k, v] of Object.entries(byGroup)) if (v > 0) out[k] = v / sum;
    return out;
  };
  return {
    allocations_long: normalise(longByGroup, sumLong),
    allocations_short: normalise(shortByGroup, sumShort),
    allocations_long_total: sumLong,
    allocations_short_total: sumShort,
    allocations_long_abs: longByGroup,
    allocations_short_abs: shortByGroup,
  };
};

/** Backend sends these trade/hold ratios as percentage-scale numbers (e.g.
 *  58.33 = 58.33%), but the studio's `pct()` helper expects fractions and
 *  multiplies by 100. Normalise to fractions so the display matches the real
 *  individual report. */
const toFraction = (v: number) => v / 100;

/** Backend `investments` blocks come back as `{ error: 'Insufficient data' }`
 *  for thin players. Swap those for the neutral placeholder so the best/worst
 *  cards render a `—` card instead of dereferencing undefined fields. */
const safeInvestment = <T extends { instrument?: string; error?: string }>(
  block: T,
  fallback: T,
): T => (block && !block.error && block.instrument != null ? block : fallback);

/* ---- returns breakdown (geography / asset class) ------------------------ */

const ASSET_CLASS_LABEL_BY_TYPE = new Map(
  instrumentTypes.map(t => [t.type, t.name]),
);

/** Map every numeric value of a `{ [k]: number }` dict through `fn`. */
const mapDictValues = (
  obj: { [k: string]: number },
  fn: (v: number) => number,
): { [k: string]: number } => {
  const out: { [k: string]: number } = {};
  for (const [k, v] of Object.entries(obj)) out[k] = fn(v);
  return out;
};

/** Re-key `return_per_asset_class` from backend class names ('Equity') to the
 *  studio's asset-class labels ('Equities') — so the bars label correctly and
 *  the click-to-drill matches the pnl_distribution (keyed by asset-class code).
 *  Values pass through `fn` (here: monetary PnL → return fraction). */
const rekeyAssetClass = (
  obj: { [k: string]: number },
  fn: (v: number) => number,
): { [k: string]: number } => {
  const out: { [k: string]: number } = {};
  for (const [type, v] of Object.entries(obj)) {
    const label = ASSET_CLASS_LABEL_BY_TYPE.get(type) ?? type;
    out[label] = (out[label] ?? 0) + fn(v);
  }
  return out;
};

/** Overlay the real report fields the Snapshot + Behaviour tabs need when a
 *  computed report is available; otherwise keep the neutral skeleton. The
 *  backend sends realized/unrealized/fx "returns" as monetary sums, so they're
 *  expressed as return fractions (÷ starting cash) to match the studio's `pct`
 *  helpers; gross profit/loss/net stay monetary for the P&L bridge. */
export const overlayReport = (
  sp: Studio2Player,
  report?: IndividualReport,
  startingCash = 0,
): Studio2Player => {
  if (!report || report.status !== REPORT_COMPUTED) return sp;
  const asReturn = (v: number) => (startingCash ? v / startingCash : 0);
  return {
    ...sp,
    report: {
      ...sp.report,
      returns: {
        ...report.returns,
        realized_return: asReturn(report.returns.realized_return),
        unrealized_return: asReturn(report.returns.unrealized_return),
        fx_return: asReturn(report.returns.fx_return),
      },
      returns_breakdown: {
        return_per_geography: mapDictValues(
          report.returns_breakdown.return_per_geography,
          asReturn,
        ),
        return_per_asset_class: rekeyAssetClass(
          report.returns_breakdown.return_per_asset_class,
          asReturn,
        ),
        return_per_instrument: report.returns_breakdown.return_per_instrument,
      },
      trades: {
        ...report.trades,
        profitable_percent: toFraction(report.trades.profitable_percent),
        losing_percent: toFraction(report.trades.losing_percent),
        intraday_percent: toFraction(report.trades.intraday_percent),
      },
      return_per_trade: report.return_per_trade,
      hold_period: {
        ...report.hold_period,
        trades_percent: toFraction(report.hold_period.trades_percent),
      },
      investments: {
        biggest_winner: safeInvestment(
          report.investments.biggest_winner,
          NEUTRAL_REPORT.investments.biggest_winner,
        ),
        biggest_loser: safeInvestment(
          report.investments.biggest_loser,
          NEUTRAL_REPORT.investments.biggest_loser,
        ),
        top_long_position: safeInvestment(
          report.investments.top_long_position,
          NEUTRAL_REPORT.investments.top_long_position,
        ),
        top_short_position: safeInvestment(
          report.investments.top_short_position,
          NEUTRAL_REPORT.investments.top_short_position,
        ),
      },
    },
  };
};

/* ---- blotter / open orders (enrich raw orders → flat studio rows) ------- */

/** Build a player's blotter + open-order rows from raw orders, joined to
 *  ticker/currency via financial_instrument → underlying_asset (mirrors v1's
 *  Blotter/OpenOrders). NOTE: the backend `order.term` field actually holds the
 *  analysis method (technical/fundamental) and `order.analysis` holds the
 *  horizon (short/medium) — the field names are inverted — so they're swapped
 *  here so the studio's Term / Analysis columns label correctly. */
/** Classify each EXECUTED order by how it changed the player's running signed
 *  position in that instrument (see blotter_decisions_columns_spec.md §1).
 *  Returns a map order.id → TradeAttribute. Per instrument the executed orders
 *  are replayed oldest→newest tracking signed quantity; the final running sum is
 *  reconciled against the instrument's current signed `amount` — if it diverges
 *  (a non-order position change: maturity, forced liquidation, corporate action)
 *  that instrument's attributes are dropped so the blotter renders "—" rather
 *  than mislabel. Reversal = a same-`scenario_date` close→opposite-direction open
 *  (the platform forbids direct direction changes, so no order crosses zero). */
export const buildTradeAttributes = (
  player: Player,
  allOrders: Order[],
  isHistorical: boolean,
): Map<number, TradeAttribute> => {
  const fiAmount = new Map<number, number>(
    Object.values(player.financial_instruments ?? {}).map(fi => [
      fi.id,
      fi.amount ?? 0,
    ]),
  );
  // Same-period key for reversal detection + chronological ordering: scenario
  // date for historical, wall-clock for real-time.
  const periodKey = (o: Order) =>
    (isHistorical ? (o.scenario_date ?? o.executed_at) : o.executed_at) ??
    o.timestamp;
  const execMoment = (o: Order) => o.executed_at ?? o.timestamp;

  const byFi = new Map<number, Order[]>();
  for (const o of allOrders) {
    if (o.status !== EXECUTED || !fiAmount.has(o.financial_instrument))
      continue;
    const arr = byFi.get(o.financial_instrument);
    if (arr) arr.push(o);
    else byFi.set(o.financial_instrument, [o]);
  }

  const result = new Map<number, TradeAttribute>();
  byFi.forEach((orders, fiId) => {
    orders.sort((a, b) => {
      const pa = periodKey(a) ?? '';
      const pb = periodKey(b) ?? '';
      if (pa !== pb) return pa < pb ? -1 : 1;
      const ma = execMoment(a) ?? '';
      const mb = execMoment(b) ?? '';
      if (ma !== mb) return ma < mb ? -1 : 1;
      return a.id - b.id;
    });

    let pos = 0;
    let lastClosePeriod: string | null = null;
    let lastCloseSign = 0;
    const local = new Map<number, TradeAttribute>();

    for (const o of orders) {
      const delta = o.transaction === BUY ? o.quantity : -o.quantity;
      const before = pos;
      const after = before + delta;
      let attr: TradeAttribute;
      if (before === 0 && after !== 0) {
        const samePeriodClose =
          lastClosePeriod != null && periodKey(o) === lastClosePeriod;
        attr =
          samePeriodClose && Math.sign(after) !== lastCloseSign
            ? 'reversal'
            : 'initiation';
      } else if (after === 0) {
        attr = 'closing';
        lastClosePeriod = periodKey(o) ?? null;
        lastCloseSign = Math.sign(before);
      } else if (Math.sign(after) !== Math.sign(before)) {
        // Single-order sign flip — the platform shouldn't allow it, but classify
        // defensively rather than mislabel as build/trim.
        attr = 'reversal';
      } else if (Math.abs(after) > Math.abs(before)) {
        attr = 'building_up';
      } else {
        attr = 'trimming_down';
      }
      local.set(o.id, attr);
      pos = after;
    }

    // Reconcile against the instrument's current signed position.
    const amount = fiAmount.get(fiId) ?? 0;
    if (Math.abs(pos - amount) <= Math.max(1, Math.abs(amount) * 1e-6)) {
      local.forEach((v, k) => result.set(k, v));
    }
  });

  return result;
};

export const buildPlayerOrders = (
  player: Player,
  allOrders: Order[],
  underlyingAssets: Record<number, UnderlyingAsset>,
  currencies: Record<number, Currency>,
  scenarioType: number,
  regimesByAsset: Map<number, MarketRegimeSegmentType[]> = new Map(),
): { blotter: Studio2BlotterRow[]; open_orders: Studio2OpenOrderRow[] } => {
  // Historical sessions stamp each order with its scenario (market) date;
  // real-time sessions only have the real clock, so show the raw `timestamp`.
  const isHistorical = scenarioType === HISTORICAL_SCENARIO;
  // Historical: scenario (market) date. Real-time: EXECUTION time (executed_at) —
  // the same axis the trade-attribute replay orders on, so the blotter's row order
  // agrees with the classifications. Falls back to `timestamp` (placement) for
  // rows without an executed_at (e.g. still-open orders). CREATED open orders have
  // no executed_at, so they naturally show placement time.
  const orderTime = (ord: Order) =>
    isHistorical
      ? (ord.scenario_date ?? ord.timestamp)
      : (ord.executed_at ?? ord.timestamp);
  const fiById = new Map(
    Object.values(player.financial_instruments ?? {}).map(fi => [fi.id, fi]),
  );
  const attrById = buildTradeAttributes(player, allOrders, isHistorical);

  // Market regime for a trade = the segment for its asset whose
  // [start_date, end_date] contains the trade's scenario date (date string).
  const regimeFor = (
    o: Order,
    fi?: { underlying_asset: number },
  ): Studio2BlotterRow['market_regime'] => {
    if (!fi) return null;
    const segs = regimesByAsset.get(fi.underlying_asset);
    if (!segs || !segs.length) return null;
    const dstr = (orderTime(o) ?? '').slice(0, 10);
    if (!dstr) return null;
    const seg = segs.find(s => s.start_date <= dstr && dstr <= s.end_date);
    if (!seg) return null;
    const meta = REGIME_META[seg.regime] ?? {
      label: seg.regime,
      color: 'default',
    };
    return {
      regime: seg.regime,
      label: meta.label,
      color: meta.color,
      start_date: seg.start_date,
      end_date: seg.end_date,
    };
  };

  const blotter: Studio2BlotterRow[] = [];
  const open_orders: Studio2OpenOrderRow[] = [];
  // Wall-clock execution moment per blotter row (keyed by row key = order id). Lets
  // the display sort tie-break by execution time — the SAME middle key the trade-
  // attribute replay uses (execMoment) — so within a market day the row order and
  // the classifications can't diverge. For historical this disambiguates same-date
  // rows; for RT it equals the primary sort key (executed_at) so it's a no-op there.
  const sortMoment = new Map<string, string>();

  allOrders
    .filter(o => fiById.has(o.financial_instrument))
    .filter(o => String(o.resourcetype) !== 'SimplifiedOptionSettlementOrder')
    .forEach(o => {
      const fi = fiById.get(o.financial_instrument);
      const asset = fi ? underlyingAssets[fi.underlying_asset] : undefined;
      const ticker = asset?.ticker ?? '—';
      const currency = asset
        ? (currencies[asset.variable_currency]?.symbol ?? '')
        : '';
      const isBond = !!asset?.resourcetype?.endsWith('Bond');
      const isSimplifiedOption = asset?.resourcetype === 'SimplifiedOption';
      const optionSnapshot = o.simplified_option_snapshot;
      // value mirrors v1 Blotter: price × qty, /100 for bonds (price = % of par).
      const valueAt = (price: number) =>
        isBond ? (price * o.quantity) / 100 : price * o.quantity;

      if (o.status === CREATED) {
        // Class-19 requests are immediate-only; never present a transient/failed request as a resting order.
        if (isSimplifiedOption) return;
        const price = o.limit_price ?? o.open_price ?? 0;
        open_orders.push({
          key: `${o.id}`,
          underlying_asset_ticker: ticker,
          resourcetype: o.resourcetype,
          transaction: o.transaction,
          currency,
          limit_price: o.limit_price ?? 0,
          quantity: o.quantity,
          value: valueAt(price),
          timestamp: orderTime(o),
          forecast: o.forecast,
          investment_rationale: o.investment_rationale,
          term: o.analysis, // swap — see note above
          analysis: o.term, // swap
          person: o.person,
        });
      } else if (o.status === EXECUTED) {
        // Blotter = realized trades ONLY. CREATED orders are pending (Open orders
        // card above); CANCELED orders (incl. auto-canceled ODA stop-loss/take-
        // profit legs and unfilled limits) never executed, so they carry no
        // open_price/attribute and would render as misleading 0-price/0-value/"—"
        // rows counted under the "N executed" header. Drop them entirely so the
        // blotter — and its count — reflect executed orders only.
        const price = o.open_price ?? 0;
        sortMoment.set(`${o.id}`, o.executed_at ?? o.timestamp);
        blotter.push({
          key: `${o.id}`,
          underlying_asset_ticker: ticker,
          transaction: o.transaction,
          currency,
          open_price: isSimplifiedOption ? 0 : (o.open_price ?? 0),
          quantity: isSimplifiedOption
            ? simplifiedOptionSnapshotContracts(optionSnapshot)
            : o.quantity,
          value: isSimplifiedOption ? 0 : valueAt(price),
          timestamp: orderTime(o),
          forecast: o.forecast,
          investment_rationale: o.investment_rationale,
          term: o.analysis, // swap
          analysis: o.term, // swap
          person: o.person,
          status: o.status,
          attribute: attrById.get(o.id) ?? null,
          market_regime: regimeFor(o, fi),
          is_simplified_option: isSimplifiedOption,
          option_action: optionSnapshot
            ? simplifiedOptionSnapshotActionLabel(
                o.transaction,
                optionSnapshot.execution_effect,
              )
            : undefined,
          option_contracts: optionSnapshot
            ? simplifiedOptionSnapshotContracts(optionSnapshot)
            : undefined,
          option_premium: optionSnapshot?.signed_total_premium,
          option_expiry_days: optionSnapshot?.days_to_expiry,
          option_payoff: optionSnapshot?.display_payoff,
        });
      }
    });

  // Newest first, keyed exactly like the attribute replay but reversed:
  //   1) the Time-column value (scenario date for historical, executed_at for RT),
  //   2) wall-clock execution moment (disambiguates same-market-day historical rows
  //      — the replay's middle key; a no-op for RT where it equals key 1),
  //   3) deterministic id tie-break (later id first — reverse of the replay's
  //      ascending id). So the row order is the exact reverse of the classifications'
  //      order and the two can't diverge within a day. `key` is the order id string.
  blotter.sort((a, b) => {
    if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? 1 : -1;
    const ma = sortMoment.get(a.key) ?? '';
    const mb = sortMoment.get(b.key) ?? '';
    if (ma !== mb) return ma < mb ? 1 : -1;
    return Number(b.key) - Number(a.key);
  });
  return { blotter, open_orders };
};

/** Nicely format the scenario (simulation) date — pinned to UTC so the stored
 *  date isn't shifted by the viewer's timezone (e.g. "4 May 2018"). */
const niceDate = (d?: string): string => {
  if (!d) return '';
  const ms = Date.parse(d);
  if (Number.isNaN(ms)) return d;
  return new Date(ms).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** Build the `Studio2Session` header from the real session: name, scenario
 *  label, reporting-currency symbol, and the simulation date (shown where the
 *  mock's "Day X / Y" interval label used to be). metrics_limits/frequency
 *  drive the risk evolution zones + x-axis. */
export const buildLiveSession = (
  s: Session,
  currencySymbol?: string,
  // Per-player (trainee) historical view: relativises the header date to
  // "Day N". Absent (trainer/cohort) → real date, unchanged.
  relativizeDate?: RelativizeDate,
  displaySurface: 'trainer' | 'trainee' = 'trainer',
): Studio2Session => ({
  // Every field assigned explicitly — NO `...fallback` spread, so no mock value
  // can leak in. Any NEW Studio2Session field must be added here explicitly.
  id: s.id,
  name: s.klass_name ?? '',
  reporting_currency: currencySymbol ?? '',
  net_dv01_limit: s.net_dv01_limit ?? null,
  gross_dv01_limit: s.gross_dv01_limit ?? null,
  scenario_label:
    s.scenario_type === HISTORICAL_SCENARIO ? 'Historical' : 'Real-time',
  scenario_type: s.scenario_type,
  interval_label:
    relativizeDate?.(s.scenario_date) ?? niceDate(s.scenario_date) ?? '',
  // Real never-computed sessions have null here — pass it through (never a
  // fabricated "last computed" date).
  analysis_computed_at: s.analysis_computed_at ?? null,
  analysis_computed_for_datetime: s.analysis_computed_for_datetime ?? null,
  // Benchmark configured (any kind)? Mirrors the backend `has_benchmark` gate
  // (analysis_functions.py): a constant, a session/individual composition, or a
  // portfolio composition. Drives the Return-vs-benchmark tile title + whether
  // the benchmark series is fed to the chart.
  has_benchmark: Boolean(
    s.benchmark_constant ||
      s.benchmark_compositions?.length ||
      s.benchmark_portfolio_compositions?.length ||
      s.allow_individual_benchmark,
  ),
  // Studio-only field the real Session doesn't carry and no consumer reads —
  // keep empty rather than fabricated.
  benchmark_history: [],
  metrics_limits: s.metrics_limits ?? [],
  frequency: s.frequency ?? 1,
  time_advancement_type: s.time_advancement_type ?? null,
  start_date: s.start_date ?? '',
  display_surface: displaySurface,
  show_bias_and_report_in_player_view:
    s.show_bias_and_report_in_player_view ?? false,
});

/** Empty session placeholder the data hooks return while the real session loads
 *  (before `isReady`). A COMPLETE `Studio2Session` — never null — so StudioShell
 *  can read `session.*` above the loading-skeleton gate without guards. Rendered
 *  only behind the skeleton, so its neutral values are never shown. */
export const NEUTRAL_SESSION: Studio2Session = {
  id: 0,
  name: '',
  reporting_currency: '',
  net_dv01_limit: null,
  gross_dv01_limit: null,
  scenario_label: '',
  scenario_type: 1,
  interval_label: '',
  analysis_computed_at: null,
  analysis_computed_for_datetime: null,
  has_benchmark: false,
  benchmark_history: [],
  metrics_limits: [],
  time_advancement_type: null,
  start_date: '',
  display_surface: 'trainer',
  frequency: 1,
  show_bias_and_report_in_player_view: false,
};

/* ---- AI reports (prompts × responses join, filtered + de-placeholdered) -- */

/** Per-player completed AI reports as `{prompt, response}` pairs — same filter
 *  as v1's `getCompletedPlayerAIReportOptions` (latest completed response per
 *  prompt, in-flight placeholders dropped), but emitting the full typed objects
 *  the studio's ReportPanel consumes (`prompt.name`, `response.response`,
 *  `response.provider`). */
export const buildPlayerAIReports = (
  promptsObj: Record<number, PromptType>,
  responses: Record<number, LLMResponseType>,
  playerId: number,
): Studio2AIReport[] => {
  const latestByPrompt = new Map<number, LLMResponseType>();
  Object.values(responses)
    .sort((a, b) => b.id - a.id)
    .forEach(r => {
      if (r.player !== playerId || !isCompletedLLMResponse(r)) return;
      if (!latestByPrompt.has(r.prompt)) latestByPrompt.set(r.prompt, r);
    });
  return Array.from(latestByPrompt.values())
    .map(response => {
      const prompt = promptsObj[response.prompt];
      return prompt ? { prompt, response } : null;
    })
    .filter((x): x is Studio2AIReport => x != null)
    .sort((a, b) => b.prompt.id - a.prompt.id);
};

/** Empty cohort report used when the session has no computed group summary. */
export const NEUTRAL_GROUP_REPORT: LLMGroupResponseType = {
  id: 0,
  prompt: 0,
  session: 0,
  group_report_name: '—',
  player_ids: [],
  response: '',
};

/** All completed cohort/group reports for the session (latest first), so the
 *  trainer can pick among them; a single neutral entry when none exist. */
export const buildGroupReports = (
  groupResponses: Record<number, LLMGroupResponseType>,
  sessionId: number,
): LLMGroupResponseType[] => {
  const list = Object.values(groupResponses)
    .filter(r => r.session === sessionId && isCompletedLLMGroupResponse(r))
    .sort((a, b) => b.id - a.id);
  return list.length ? list : [NEUTRAL_GROUP_REPORT];
};

/* ---- bias reports (one per player → map keyed by player id) ------------- */

/** Convert the store's `BiasReportType` records into the studio view-model,
 *  keyed by player id. Backend `band`/`overall_band` are already 0-3 integers
 *  (the model stores PositiveSmallIntegerField), so they pass through directly. */
export const buildBiasByPlayer = (
  biasReports: Record<number, BiasReportType>,
): Map<number, Studio2BiasReport> => {
  const map = new Map<number, Studio2BiasReport>();
  Object.values(biasReports).forEach(r => {
    map.set(r.player, {
      id: r.id,
      player: r.player,
      session: r.session,
      provider: r.provider,
      status: r.status,
      overall_band: r.overall_band,
      narrative: r.narrative,
      generated_at: r.generated_at,
      findings: (r.findings ?? []).map(f => ({
        id: f.id,
        category: f.category,
        bias_type: f.bias_type,
        severity: f.severity,
        band: f.band,
        confidence: f.confidence,
        summary: f.summary,
        evidence: (f.evidence ?? []).map(e => ({
          scenario_date: e.scenario_date,
          asset: e.asset,
          direction: e.direction,
          note: e.note,
        })),
      })),
    });
  });
  return map;
};

/* ---- rank (cohort-derived; backend never computes IndividualReport.rank) -- */

/** Rank players by leaderboard `total_score` (desc) when available, else by
 *  portfolio return, and inject the 1-based rank into each player's
 *  report.returns (a fresh `returns` object per player so the shared neutral
 *  report isn't mutated). `LeaderboardScoresType.player_id` is a string, so it's
 *  coerced to match the numeric player id. */
export const withRanks = (
  players: Studio2Player[],
  leaderboard: LeaderboardScoresType[],
): Studio2Player[] => {
  const orderedIds = leaderboard.length
    ? [...leaderboard]
        .sort((a, b) => b.total_score - a.total_score)
        .map(s => Number(s.player_id))
    : [...players]
        .sort((a, b) => b.last_portfolio_return - a.last_portfolio_return)
        .map(p => p.id);
  const rankById = new Map(orderedIds.map((id, i) => [id, i + 1]));
  return players.map(p => ({
    ...p,
    report: {
      ...p.report,
      returns: { ...p.report.returns, rank: rankById.get(p.id) ?? 0 },
    },
  }));
};

/** Inputs needed to assemble the studio payload from the raw redux slices.
 *  The cohort (trainer) and self (trainee) hooks differ ONLY in `roster` (the
 *  whole roster vs `[me]`) and `leaderboard` (the cohort scores vs `[]`); every
 *  other field is identical, which is why both share this one pure builder. */
export type AssembleStudioInput = {
  roster: Player[];
  session: Session;
  analysisItems: PlayerAnalysisType[];
  biasReports: Record<number, BiasReportType>;
  promptsObj: Record<number, PromptType>;
  responses: Record<number, LLMResponseType>;
  groupResponses: Record<number, LLMGroupResponseType>;
  allOrders: Order[];
  underlyingAssets: Record<number, UnderlyingAsset>;
  currencies: Record<number, Currency>;
  focusedId?: number;
  focusedReport?: IndividualReport;
  currencySymbol?: string;
  leaderboard: LeaderboardScoresType[];
  // Per-player (trainee) historical view only: relativises the session header
  // date to "Day N". Trainer/cohort passes nothing → real dates preserved.
  relativizeDate?: RelativizeDate;
  // Which surface is assembling: predetermined-session knits plot on an absolute datetime axis
  // for 'trainer' and a relative days-since-start axis for 'trainee'. Defaults to 'trainer'.
  displaySurface?: 'trainer' | 'trainee';
};

export type AssembledStudio = {
  players: Studio2Player[];
  session: Studio2Session;
  groupReports: LLMGroupResponseType[];
};

/** Pure assembly of `{ players, session, groupReports }` from redux slices —
 *  shared verbatim by the trainer (cohort) and trainee (self) data hooks. The
 *  per-player index Maps tolerate a roster of one, so the self view passes a
 *  single-element `roster` (and an empty `leaderboard`, making the rank a no-op
 *  the self UI never renders). The concentration / pnl / turnover / regime
 *  slices are read straight off `session` (already scoped server-side to the
 *  requester, so for a trainee they contain only their own rows). */
export const assembleStudioData = (
  input: AssembleStudioInput,
): AssembledStudio => {
  const {
    roster,
    session,
    analysisItems,
    biasReports,
    promptsObj,
    responses,
    groupResponses,
    allOrders,
    underlyingAssets,
    currencies,
    focusedId,
    focusedReport,
    currencySymbol,
    leaderboard,
    relativizeDate,
    displaySurface,
  } = input;

  const analysisByPlayer = new Map(analysisItems.map(a => [a.player_id, a]));
  const concentrationByPlayer = new Map(
    (session.concentrations?.data ?? []).map(c => [
      c.player_id,
      c.player_concentrations,
    ]),
  );
  const pnlByPlayer = new Map(
    (session.pnl_distributions?.data ?? []).map(d => [
      d.player_id,
      d.distributions,
    ]),
  );
  const turnoverByPlayer = new Map(
    (session.turnover?.data ?? []).map(t => [t.player_id, t]),
  );
  const biasByPlayer = buildBiasByPlayer(biasReports);
  // Market-regime segments grouped by asset (matched per blotter trade).
  const regimesByAsset = new Map<number, MarketRegimeSegmentType[]>();
  (session.market_regimes?.segments ?? []).forEach(s => {
    const arr = regimesByAsset.get(s.underlying_asset);
    if (arr) arr.push(s);
    else regimesByAsset.set(s.underlying_asset, [s]);
  });

  const players = roster.map(p => {
    const base = {
      ...neutralStudio2Player(
        p,
        analysisByPlayer.get(p.id),
        session.starting_cash_amount,
      ),
      ai_reports: buildPlayerAIReports(promptsObj, responses, p.id),
      concentration: concentrationByPlayer.get(p.id) ?? [],
      pnl_distribution: pnlByPlayer.get(p.id) ?? [],
      asset_class_turnover:
        turnoverByPlayer.get(p.id)?.asset_class_turnover ?? [],
      total_turnover: turnoverByPlayer.get(p.id)?.total_reporting_turnover ?? 0,
      turnover_computed: turnoverByPlayer.get(p.id)?.turnover_computed ?? false,
      bias: biasByPlayer.get(p.id) ?? null,
    };
    if (p.id !== focusedId) return base;
    // Orders are enriched only for the focused player (the only one shown on the
    // Decisions tab).
    const { blotter, open_orders } = buildPlayerOrders(
      p,
      allOrders,
      underlyingAssets,
      currencies,
      session.scenario_type,
      regimesByAsset,
    );
    return {
      ...overlayReport(base, focusedReport, session.starting_cash_amount),
      blotter,
      open_orders,
    };
  });

  return {
    players: withRanks(players, leaderboard),
    session: buildLiveSession(
      session,
      currencySymbol,
      relativizeDate,
      displaySurface,
    ),
    groupReports: buildGroupReports(groupResponses, session.id),
  };
};
